import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  EMPLOYMENT_TYPES, CURRENCIES, APPLICATION_STATUSES, FEER_LEVELS, OFFER_DURATIONS,
  applicationStatusLabel, employmentTypeLabel,
} from "@/lib/constants";
import { MapPin, Trash2, XCircle, Users, Sparkles, MessageSquare } from "lucide-react";
import InterviewScheduler from "@/components/InterviewScheduler";
import NexaProfileSection from "@/components/NexaProfileSection";
import EmployerAnalytics from "@/components/dashboards/EmployerAnalytics";
import EmployerCompany from "@/components/dashboards/EmployerCompany";
import EmployerInterviews from "@/components/dashboards/EmployerInterviews";

const EMPTY_FORM = {
  title: "",
  company: "",
  description: "",
  country: "",
  city: "",
  employment_type: "temps_plein",
  salary: "",
  salary_currency: "CAD",
  sector: "",
  skills: "",
  languages: "",
  experience: "",
  education: "",
  positions: 1,
  remote: false,
  noc_code: "",
  feer_level: "",
  duration_days: 30,
};

export default function EmployerDashboard() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [offers, setOffers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [offersRes, appsRes] = await Promise.all([
        api.get("/offers/mine"),
        api.get("/applications/received"),
      ]);
      setOffers(offersRes.data);
      setApplications(appsRes.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await api.post("/offers", {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        positions: Number(form.positions) || 1,
        feer_level: form.feer_level === "" ? null : Number(form.feer_level),
        duration_days: Number(form.duration_days) || 30,
      });
      await loadAll();
      setSuccess("Offre publiée avec succès.");
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const closeOffer = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/close`);
      await loadAll();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteOffer = async (offerId) => {
    try {
      await api.delete(`/offers/${offerId}`);
      await loadAll();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const updateStatus = async (applicationId, status) => {
    try {
      await api.patch(`/applications/${applicationId}/status`, { status });
      await loadAll();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent";

  return (
    <div data-testid="employer-dashboard" className="space-y-8">
      <NexaProfileSection />
      <EmployerAnalytics />
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Offer form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-brand-navy mb-5">Publier une offre</h2>

          {error && (
            <div data-testid="offer-form-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}
          {success && (
            <div data-testid="offer-form-success" className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{success}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="offer-title-input" required value={form.title} onChange={set("title")} placeholder="Titre du poste *" className={inputCls} />
              <input data-testid="offer-company-input" required value={form.company} onChange={set("company")} placeholder="Entreprise *" className={inputCls} />
            </div>
            <textarea
              data-testid="offer-description-input"
              required
              value={form.description}
              onChange={set("description")}
              placeholder="Description du poste *"
              rows={4}
              className={inputCls}
            />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="offer-country-input" required value={form.country} onChange={set("country")} placeholder="Pays *" className={inputCls} />
              <input data-testid="offer-city-input" value={form.city} onChange={set("city")} placeholder="Ville" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="offer-type-select" value={form.employment_type} onChange={set("employment_type")} className={`${inputCls} bg-white`}>
                {EMPLOYMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <input data-testid="offer-sector-input" value={form.sector} onChange={set("sector")} placeholder="Secteur (ex. Restauration)" className={inputCls} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="offer-salary-input" value={form.salary} onChange={set("salary")} placeholder="Salaire (ex. 45 000/an)" className={`${inputCls} col-span-2`} />
              <select data-testid="offer-currency-select" value={form.salary_currency} onChange={set("salary_currency")} className={`${inputCls} bg-white`}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <input data-testid="offer-skills-input" value={form.skills} onChange={set("skills")} placeholder="Compétences recherchées (séparées par des virgules)" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="offer-languages-input" value={form.languages} onChange={set("languages")} placeholder="Langues requises" className={inputCls} />
              <input data-testid="offer-positions-input" type="number" min={1} value={form.positions} onChange={set("positions")} placeholder="Nb de postes" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="offer-experience-input" value={form.experience} onChange={set("experience")} placeholder="Expérience (ex. 2 ans)" className={inputCls} />
              <input data-testid="offer-education-input" value={form.education} onChange={set("education")} placeholder="Formation requise" className={inputCls} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="offer-noc-input" value={form.noc_code} onChange={set("noc_code")} placeholder="Code CNP/NOC (ex. 64100)" className={inputCls} />
              <select data-testid="offer-feer-select" value={form.feer_level} onChange={set("feer_level")} className={`${inputCls} bg-white`}>
                <option value="">Niveau FEER (optionnel)</option>
                {FEER_LEVELS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <select data-testid="offer-duration-select" value={form.duration_days} onChange={set("duration_days")} className={`${inputCls} bg-white`}>
                {OFFER_DURATIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                data-testid="offer-remote-checkbox"
                type="checkbox"
                checked={form.remote}
                onChange={(e) => setForm((f) => ({ ...f, remote: e.target.checked }))}
                className="w-4 h-4 accent-brand-blue"
              />
              Télétravail possible
            </label>
            <button
              data-testid="offer-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-50"
            >
              {submitting ? "Publication…" : "Publier l'offre"}
            </button>
          </form>
        </div>

        {/* My offers */}
        <div>
          <h2 className="font-semibold text-brand-navy mb-4">Mes offres ({offers.length})</h2>
          <div className="space-y-3">
            {offers.map((o) => (
              <div key={o.offer_id} data-testid={`offer-card-${o.offer_id}`} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-brand-navy">{o.title}</p>
                    <p className="text-sm text-slate-500">{o.company}</p>
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {[o.city, o.country].filter(Boolean).join(", ")} · {employmentTypeLabel(o.employment_type)}
                      {o.salary ? ` · ${o.salary} ${o.salary_currency || ""}` : ""}
                      {o.expires_at ? ` · Expire le ${new Date(o.expires_at).toLocaleDateString("fr-CA")}` : ""}
                    </p>
                  </div>
                  <span
                    data-testid={`offer-status-${o.offer_id}`}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      o.status === "active" ? "bg-green-50 text-green-700" : o.status === "expired" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {o.status === "active" ? "Active" : o.status === "expired" ? "Expirée" : "Fermée"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Link
                    to={`/offers/${o.offer_id}/matches`}
                    data-testid={`offer-matches-${o.offer_id}`}
                    className="flex items-center gap-1.5 text-xs font-medium text-brand-blue border border-brand-blue/30 rounded-lg px-3 py-1.5 hover:bg-brand-sky"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Matching IA
                  </Link>
                  {o.status === "active" && (
                    <button
                      data-testid={`offer-close-${o.offer_id}`}
                      onClick={() => closeOffer(o.offer_id)}
                      className="flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Fermer
                    </button>
                  )}
                  <button
                    data-testid={`offer-delete-${o.offer_id}`}
                    onClick={() => deleteOffer(o.offer_id)}
                    className="flex items-center gap-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
            {offers.length === 0 && (
              <p data-testid="offers-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-8 text-center">
                Aucune offre publiée pour le moment.
              </p>
            )}
          </div>
        </div>
      </div>

      <EmployerCompany />

      {/* Received applications */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100 flex items-center gap-2">
          <Users className="w-5 h-5 text-brand-blue" />
          <h2 className="font-semibold text-brand-navy">Candidatures reçues ({applications.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">Candidat</th>
                <th className="px-5 py-3 font-medium">Offre</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Statut</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {applications.map((a) => (
                <tr key={a.application_id} data-testid={`received-application-${a.application_id}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <p className="font-medium text-brand-navy">{a.candidate_name}</p>
                    <p className="text-xs text-slate-400">{a.candidate_email}</p>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{a.offer_title}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-CA") : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <select
                      data-testid={`application-status-select-${a.application_id}`}
                      value={a.status}
                      onChange={(e) => updateStatus(a.application_id, e.target.value)}
                      className="border border-slate-200 rounded-md px-2 py-1 text-xs bg-white"
                    >
                      {APPLICATION_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <InterviewScheduler applicationId={a.application_id} onScheduled={loadAll} />
                      <Link
                        to={`/messages/${a.application_id}`}
                        data-testid={`received-message-${a.application_id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-turquoise border border-brand-turquoise/30 rounded-lg px-3 py-1.5 hover:bg-brand-turquoise/10"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Message
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {applications.length === 0 && (
                <tr>
                  <td colSpan={5} data-testid="applications-empty" className="px-5 py-8 text-center text-slate-400">
                    Aucune candidature reçue pour le moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EmployerInterviews />
    </div>
  );
}
